import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  ArrowLeft, Zap, TrendingUp, DollarSign, Home, Percent,
  CheckCircle2, AlertTriangle, XCircle, ArrowRight, Calculator,
  Clock, Target, Sparkles
} from "lucide-react";
import { ChatToggleButton } from "@/components/AIChatWidget";

// Smart defaults for everything else - moved OUTSIDE component to prevent recreation
const DEFAULTS = {
  loanTermYears: 30,
  closingCostPercent: 2,
  vacancyRate: 8,
  maintenancePercent: 5,
  capexPercent: 5,
  managementPercent: 10,
  insuranceAnnual: 1200,
  propertyTaxRate: 1.2, // percent of purchase price
};

export default function QuickScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Only 5 essential inputs
  const [inputs, setInputs] = useState({
    purchasePrice: 300000,
    monthlyRent: 2500,
    downPaymentPercent: 20,
    interestRate: 7,
    repairBudget: 0
  });

  // Calculate everything live as inputs change
  const analysis = useMemo(() => {
    const { purchasePrice, monthlyRent, downPaymentPercent, interestRate, repairBudget } = inputs;
    
    // Financing calculations
    const downPayment = purchasePrice * (downPaymentPercent / 100);
    const closingCosts = purchasePrice * (DEFAULTS.closingCostPercent / 100);
    const loanAmount = purchasePrice - downPayment;
    const totalCashInvested = downPayment + closingCosts + repairBudget;
    
    // Monthly mortgage payment (P&I)
    const monthlyRate = (interestRate / 100) / 12;
    const numPayments = DEFAULTS.loanTermYears * 12;
    const monthlyMortgage = loanAmount > 0 
      ? loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1)
      : 0;
    
    // Operating expenses (monthly)
    const vacancyLoss = monthlyRent * (DEFAULTS.vacancyRate / 100);
    const effectiveRent = monthlyRent - vacancyLoss;
    const maintenance = monthlyRent * (DEFAULTS.maintenancePercent / 100);
    const capex = monthlyRent * (DEFAULTS.capexPercent / 100);
    const management = monthlyRent * (DEFAULTS.managementPercent / 100);
    const insurance = DEFAULTS.insuranceAnnual / 12;
    const propertyTax = (purchasePrice * (DEFAULTS.propertyTaxRate / 100)) / 12;
    
    const totalExpenses = maintenance + capex + management + insurance + propertyTax;
    const noi = effectiveRent - totalExpenses;
    const monthlyCashFlow = noi - monthlyMortgage;
    const annualCashFlow = monthlyCashFlow * 12;
    
    // Key metrics
    const cashOnCash = totalCashInvested > 0 ? (annualCashFlow / totalCashInvested) * 100 : 0;
    const capRate = purchasePrice > 0 ? ((noi * 12) / purchasePrice) * 100 : 0;
    const grossYield = purchasePrice > 0 ? ((monthlyRent * 12) / purchasePrice) * 100 : 0;
    const dscr = monthlyMortgage > 0 ? noi / monthlyMortgage : 0;
    const onePercentRule = purchasePrice > 0 ? (monthlyRent / purchasePrice) * 100 : 0;
    
    // Deal Score (0-10)
    let score = 5; // Start neutral
    
    // Cash flow scoring (+/- 2 points)
    if (monthlyCashFlow >= 300) score += 2;
    else if (monthlyCashFlow >= 150) score += 1;
    else if (monthlyCashFlow >= 0) score += 0;
    else if (monthlyCashFlow >= -100) score -= 1;
    else score -= 2;
    
    // Cash on Cash scoring (+/- 2 points)
    if (cashOnCash >= 12) score += 2;
    else if (cashOnCash >= 8) score += 1.5;
    else if (cashOnCash >= 5) score += 0.5;
    else if (cashOnCash >= 0) score += 0;
    else score -= 1;
    
    // 1% Rule scoring (+/- 1 point)
    if (onePercentRule >= 1) score += 1;
    else if (onePercentRule >= 0.8) score += 0.5;
    else if (onePercentRule >= 0.6) score += 0;
    else score -= 0.5;
    
    // DSCR scoring (+/- 1 point)
    if (dscr >= 1.5) score += 1;
    else if (dscr >= 1.25) score += 0.5;
    else if (dscr >= 1) score += 0;
    else score -= 1;
    
    // Clamp score between 0-10
    score = Math.max(0, Math.min(10, score));
    
    // Traffic light determination
    let signal = 'red';
    let signalText = 'Lower Metric Score';
    if (score >= 7) {
      signal = 'green';
      signalText = 'Higher Metric Score';
    } else if (score >= 5) {
      signal = 'yellow';
      signalText = 'Mixed Metric Score';
    }
    
    return {
      // Inputs summary
      downPayment,
      closingCosts,
      loanAmount,
      totalCashInvested,
      monthlyMortgage,
      
      // Results
      monthlyCashFlow,
      annualCashFlow,
      cashOnCash,
      capRate,
      grossYield,
      dscr,
      onePercentRule,
      noi,
      
      // Scoring
      score: Math.round(score * 10) / 10,
      signal,
      signalText
    };
  }, [inputs]);

  const handleInputChange = (field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const goToDeepDive = () => {
    // Navigate to full rental analysis with pre-filled values
    navigate('/rental-analysis', { 
      state: { 
        prefill: {
          purchase_price: inputs.purchasePrice,
          monthly_rent: inputs.monthlyRent,
          down_payment_percent: inputs.downPaymentPercent,
          interest_rate: inputs.interestRate,
          rehab_costs: inputs.repairBudget
        }
      }
    });
  };

  const SignalIcon = analysis.signal === 'green' ? CheckCircle2 
    : analysis.signal === 'yellow' ? AlertTriangle 
    : XCircle;

  const signalColors = {
    green: 'text-emerald-500 bg-emerald-500/20 border-emerald-500/30',
    yellow: 'text-amber-500 bg-amber-500/20 border-amber-500/30',
    red: 'text-red-500 bg-red-500/20 border-red-500/30'
  };

  return (
    <div className="min-h-screen bg-slate-900" data-testid="quick-screen-page">
      {/* Header */}
      <header className="glass border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate("/dashboard")}
                className="text-slate-400 hover:text-white"
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <div className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-amber-500" />
                <h1 className="text-lg font-semibold text-white font-['Manrope']">Quick Screen</h1>
                <span className="px-2 py-0.5 text-xs font-bold bg-amber-500/20 text-amber-500 rounded flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  5 MIN
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Intro */}
        <div className="text-center mb-8">
          <p className="text-slate-400 max-w-xl mx-auto">
            Screen deals in seconds. Enter just 5 numbers and instantly see if a property is worth pursuing.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Panel */}
          <div className="glass-card rounded-xl p-6">
            <div className="flex items-center gap-2 mb-6">
              <Sparkles className="h-5 w-5 text-amber-500" />
              <h2 className="text-lg font-semibold text-white">5 Essential Inputs</h2>
            </div>

            <div className="space-y-6">
              {/* Purchase Price */}
              <div data-testid="purchase-price-slider">
                <div className="flex justify-between mb-2">
                  <Label className="text-slate-300 flex items-center gap-2">
                    <Home className="h-4 w-4 text-slate-500" />
                    Purchase Price
                  </Label>
                  <span className="text-amber-500 font-mono font-semibold" data-testid="purchase-price-value">
                    {formatCurrency(inputs.purchasePrice)}
                  </span>
                </div>
                <Slider
                  value={[inputs.purchasePrice]}
                  onValueChange={([v]) => handleInputChange('purchasePrice', v)}
                  min={50000}
                  max={2000000}
                  step={5000}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>$50K</span>
                  <span>$2M</span>
                </div>
              </div>

              {/* Monthly Rent */}
              <div>
                <div className="flex justify-between mb-2">
                  <Label className="text-slate-300 flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-slate-500" />
                    Monthly Rent
                  </Label>
                  <span className="text-amber-500 font-mono font-semibold">
                    {formatCurrency(inputs.monthlyRent)}
                  </span>
                </div>
                <Slider
                  value={[inputs.monthlyRent]}
                  onValueChange={([v]) => handleInputChange('monthlyRent', v)}
                  min={500}
                  max={10000}
                  step={50}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>$500</span>
                  <span>$10K</span>
                </div>
              </div>

              {/* Down Payment */}
              <div>
                <div className="flex justify-between mb-2">
                  <Label className="text-slate-300 flex items-center gap-2">
                    <Percent className="h-4 w-4 text-slate-500" />
                    Down Payment
                  </Label>
                  <span className="text-amber-500 font-mono font-semibold">
                    {inputs.downPaymentPercent}%
                  </span>
                </div>
                <Slider
                  value={[inputs.downPaymentPercent]}
                  onValueChange={([v]) => handleInputChange('downPaymentPercent', v)}
                  min={0}
                  max={100}
                  step={5}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>0%</span>
                  <span>100%</span>
                </div>
              </div>

              {/* Interest Rate */}
              <div>
                <div className="flex justify-between mb-2">
                  <Label className="text-slate-300 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-slate-500" />
                    Interest Rate
                  </Label>
                  <span className="text-amber-500 font-mono font-semibold">
                    {inputs.interestRate}%
                  </span>
                </div>
                <Slider
                  value={[inputs.interestRate]}
                  onValueChange={([v]) => handleInputChange('interestRate', v)}
                  min={3}
                  max={12}
                  step={0.125}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>3%</span>
                  <span>12%</span>
                </div>
              </div>

              {/* Repair Budget */}
              <div>
                <div className="flex justify-between mb-2">
                  <Label className="text-slate-300 flex items-center gap-2">
                    <Calculator className="h-4 w-4 text-slate-500" />
                    Repair Budget
                  </Label>
                  <span className="text-amber-500 font-mono font-semibold">
                    {formatCurrency(inputs.repairBudget)}
                  </span>
                </div>
                <Slider
                  value={[inputs.repairBudget]}
                  onValueChange={([v]) => handleInputChange('repairBudget', v)}
                  min={0}
                  max={100000}
                  step={1000}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>$0</span>
                  <span>$100K</span>
                </div>
              </div>
            </div>

            {/* Smart Defaults Info */}
            <div className="mt-6 p-3 bg-slate-800/50 rounded-lg">
              <p className="text-xs text-slate-400">
                <strong className="text-slate-300">Smart Defaults Applied:</strong> 8% vacancy, 5% maintenance, 5% CapEx, 10% management, 30-year loan, 2% closing costs
              </p>
            </div>
          </div>

          {/* Results Panel */}
          <div className="space-y-6">
            {/* Traffic Light Signal */}
            <div className={`glass-card rounded-xl p-6 border-2 ${signalColors[analysis.signal]}`} data-testid="traffic-light-indicator">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-full ${signalColors[analysis.signal]}`}>
                    <SignalIcon className="h-8 w-8" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white" data-testid="signal-text">{analysis.signalText}</h3>
                    <p className="text-slate-400">Quick Assessment</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-4xl font-bold text-white" data-testid="deal-score">{analysis.score}</div>
                  <div className="text-slate-400 text-sm">/ 10</div>
                </div>
              </div>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-2 gap-4">
              {/* Monthly Cash Flow */}
              <div className="glass-card rounded-xl p-4" data-testid="monthly-cash-flow-card">
                <div className="text-sm text-slate-400 mb-1">Monthly Cash Flow</div>
                <div className={`text-2xl font-bold font-mono ${analysis.monthlyCashFlow >= 0 ? 'text-emerald-500' : 'text-red-500'}`} data-testid="monthly-cash-flow-value">
                  {formatCurrency(analysis.monthlyCashFlow)}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {formatCurrency(analysis.annualCashFlow)}/year
                </div>
              </div>

              {/* Cash on Cash */}
              <div className="glass-card rounded-xl p-4" data-testid="coc-return-card">
                <div className="text-sm text-slate-400 mb-1">Cash on Cash Return</div>
                <div className={`text-2xl font-bold font-mono ${analysis.cashOnCash >= 8 ? 'text-emerald-500' : analysis.cashOnCash >= 5 ? 'text-amber-500' : 'text-red-500'}`} data-testid="coc-return-value">>
                  {analysis.cashOnCash.toFixed(1)}%
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  Target: 8%+
                </div>
              </div>

              {/* Cap Rate */}
              <div className="glass-card rounded-xl p-4">
                <div className="text-sm text-slate-400 mb-1">Cap Rate</div>
                <div className={`text-2xl font-bold font-mono ${analysis.capRate >= 6 ? 'text-emerald-500' : analysis.capRate >= 4 ? 'text-amber-500' : 'text-slate-300'}`}>
                  {analysis.capRate.toFixed(1)}%
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  Market: 4-8%
                </div>
              </div>

              {/* 1% Rule */}
              <div className="glass-card rounded-xl p-4">
                <div className="text-sm text-slate-400 mb-1">1% Rule</div>
                <div className={`text-2xl font-bold font-mono ${analysis.onePercentRule >= 1 ? 'text-emerald-500' : analysis.onePercentRule >= 0.8 ? 'text-amber-500' : 'text-red-500'}`}>
                  {analysis.onePercentRule.toFixed(2)}%
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  Target: 1%+
                </div>
              </div>
            </div>

            {/* Investment Summary */}
            <div className="glass-card rounded-xl p-4">
              <h4 className="text-sm font-semibold text-slate-300 mb-3">Investment Summary</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Down Payment</span>
                  <span className="text-white font-mono">{formatCurrency(analysis.downPayment)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Closing Costs</span>
                  <span className="text-white font-mono">{formatCurrency(analysis.closingCosts)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Repairs</span>
                  <span className="text-white font-mono">{formatCurrency(inputs.repairBudget)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Monthly Mortgage</span>
                  <span className="text-white font-mono">{formatCurrency(analysis.monthlyMortgage)}</span>
                </div>
                <div className="col-span-2 border-t border-slate-700 pt-2 mt-1 flex justify-between">
                  <span className="text-slate-300 font-semibold">Total Cash Needed</span>
                  <span className="text-amber-500 font-mono font-bold">{formatCurrency(analysis.totalCashInvested)}</span>
                </div>
              </div>
            </div>

            {/* Deep Dive Button */}
            <Button
              onClick={goToDeepDive}
              className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold py-6 text-lg"
              data-testid="deep-dive-btn"
            >
              <Target className="h-5 w-5 mr-2" />
              Deep Dive Analysis
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
            <p className="text-center text-xs text-slate-500">
              Estimates are based on supplied assumptions and are not financial or
              investment advice. Run full projections and perform independent due diligence.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
